import { useEffect, useState } from 'react';
import type React from 'react';

import { Text } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams, useNavigate } from '@change-corgi/core/react/router';

import { useEndorsements } from 'src/app/shared/components/endorsements';
import { ImageUploadProvider } from 'src/app/shared/hooks/imageUpload';
import { useToast } from 'src/app/shared/toast';

import { useEditEndorsement } from '../../hooks/useEditEndorsement';
import { EndorsementForm } from '../EndorsementForm';
import { EndorsementFormHeader } from '../EndorsementForm/Header';

import { contentContainer, formSection, pageContainer, previewSection } from './EditEndorsement.styles';

const EditEndorsementContent: React.FC = () => {
	const { slug, endorsementId } = useKnownAvailableParams<{ slug: string; endorsementId: string }>();
	const { translate } = useI18n();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

	const {
		data: { endorsements, isLoading: isLoadingEndorsements },
	} = useEndorsements({ petitionSlugOrId: slug });

	const endorsement = endorsements.find((e) => e.id === endorsementId);

	const { data, actions, uploader } = useEditEndorsement({
		endorsementId,
		onSuccess: () => {
			navigate(`/p/${slug}/edit?endorsementUpdated=true#endorsements`);
		},
	});

	// Show error toast when error occurs
	useEffect(() => {
		if (data.error) {
			showToast({
				variant: 'error',
				duration: 5000,
				message: data.error.message,
			});
			actions.clearError();
		}
	}, [data.error, showToast, actions]);

	useEffect(() => {
		if (endorsement && !isLoadingEndorsements) {
			const { handleFieldChange, handlePhotoUpload } = actions;
			handleFieldChange('name', endorsement.name);
			handleFieldChange('title', endorsement.title || '');
			handleFieldChange('url', endorsement.url || '');
			handleFieldChange('statement', endorsement.content || '');
			if (endorsement.photo?.userSmall.url) {
				handlePhotoUpload(endorsement.photo.userSmall.url);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endorsement?.id, isLoadingEndorsements]);

	const handleBack = () => {
		navigate(`/p/${slug}/edit#endorsements`);
	};
	const handleConfirmSubmit = async () => {
		await actions.handleSubmit();
	};

	if (isLoadingEndorsements) {
		return (
			<PageLayout
				title={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.page_title')}
				onBack={handleBack}
			>
				<div className="flex items-center justify-center p-8">
					<Text variant="body">Loading...</Text>
				</div>
			</PageLayout>
		);
	}

	if (!endorsement) {
		return (
			<PageLayout
				title={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.page_title')}
				onBack={handleBack}
			>
				<div className="flex items-center justify-center p-8">
					<Text variant="body" className="text-typography-danger">
						Endorsement not found
					</Text>
				</div>
			</PageLayout>
		);
	}

	return (
		<EndorsementForm
			title={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.page_title')}
			backButtonAriaLabel={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.back_button_aria_label')}
			formData={data.formData}
			statementCharCount={data.statementCharCount}
			statementMaxLength={data.statementMaxLength}
			isFormValid={data.formData.name.trim().length > 0 && data.formData.title.trim().length > 0}
			isSubmitting={data.isSubmitting}
			isHelpModalOpen={isHelpModalOpen}
			isSubmitModalOpen={isSubmitModalOpen}
			onBack={handleBack}
			onFieldChange={actions.handleFieldChange}
			onNext={() => setIsSubmitModalOpen(true)}
			onHelpClick={() => setIsHelpModalOpen(true)}
			onCloseHelpModal={() => setIsHelpModalOpen(false)}
			onCloseSubmitModal={() => setIsSubmitModalOpen(false)}
			onConfirmSubmit={handleConfirmSubmit}
			pageContainer={pageContainer()}
			contentContainer={contentContainer()}
			formSection={formSection()}
			previewSection={previewSection()}
			testId="edit-endorsement-page"
			uppy={uploader.uppy}
			image={data.image ?? data.formData.photoUrl}
		/>
	);
};

export const EditEndorsement: React.FC = () => {
	return (
		<ImageUploadProvider>
			<EditEndorsementContent />
		</ImageUploadProvider>
	);
};

type PageLayoutProps = Readonly<{
	title: string;
	onBack: () => void;
	children: React.ReactNode;
}>;

const PageLayout: React.FC<PageLayoutProps> = ({ title, onBack, children }) => {
	const { translate } = useI18n();
	return (
		<div className={pageContainer()}>
			<EndorsementFormHeader
				onBack={onBack}
				title={title}
				backButtonAriaLabel={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.back_button_aria_label')}
			/>
			{children}
		</div>
	);
};
